/**
 * Topbar notification bell (statute tracker, roadmap §3.6): polls the unread
 * count and drops down the most recent alerts. Clicking one marks it read and
 * opens it — a change alert's detail page (the redline and what it affects), a
 * docket entry's or deadline reminder's matter — via the shared
 * `notificationHref`; an alert with nowhere to go is just marked read.
 * Reuses the msg-menu popover pattern (ChatPage) — absolutely positioned card,
 * outside-click close — flipped to open downward from the topbar.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { notificationHref } from '@/lib/alerts';
import { Icon } from './icons';
import type { NotificationView } from '@/lib/types';

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.notifications.unreadCount(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const recent = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => api.notifications.list(0, 8),
    enabled: open,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['notifications'] });
  const markRead = useMutation({ mutationFn: (id: string) => api.notifications.markRead(id), onSuccess: invalidate });
  const markAllRead = useMutation({ mutationFn: () => api.notifications.markAllRead(), onSuccess: invalidate });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const count = unread.data?.count ?? 0;

  return (
    <div className="msg-menu" ref={ref}>
      <button
        className="menu-btn"
        style={{ display: 'inline-flex', position: 'relative' }}
        onClick={() => setOpen((v) => !v)}
        aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon.Bell />
        {count > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute', top: 2, right: 0, minWidth: 15, height: 15, padding: '0 4px',
              borderRadius: 'var(--r-full)', background: 'var(--accent)', color: 'var(--paper)',
              fontSize: 10, fontWeight: 700, lineHeight: '15px', textAlign: 'center',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="msg-menu-list" role="menu" style={{ top: '100%', bottom: 'auto', left: 'auto', right: 0, marginTop: 6, width: 340, maxWidth: '90vw' }}>
          <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between', padding: 'var(--s2) var(--s3)', borderBottom: '1px solid var(--line)' }}>
            <span className="t-sm fw-semibold">Notifications</span>
            {count > 0 && (
              <button className="inline-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-xs)' }} onClick={() => markAllRead.mutate()}>
                Mark all read
              </button>
            )}
          </div>
          {recent.isPending ? (
            <div className="muted" style={{ padding: 'var(--s3)', fontSize: 'var(--fs-sm)' }}>Loading…</div>
          ) : (recent.data?.items.length ?? 0) === 0 ? (
            <div className="muted" style={{ padding: 'var(--s3)', fontSize: 'var(--fs-sm)' }}>
              No notifications yet. Watch an authority and ALFIE will alert you when it changes.
            </div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {recent.data!.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  href={notificationHref(n)}
                  onOpen={() => {
                    if (n.readAt == null) markRead.mutate(n.id);
                    const href = notificationHref(n);
                    if (href) {
                      setOpen(false);
                      navigate(href);
                    }
                  }}
                />
              ))}
            </div>
          )}
          <div style={{ padding: 'var(--s2) var(--s3)', borderTop: '1px solid var(--line)' }}>
            <Link to="/watches" className="inline-link t-xs" onClick={() => setOpen(false)}>
              Manage watches →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, href, onOpen }: { n: NotificationView; href: string | null; onOpen: () => void }) {
  const isUnread = n.readAt == null;
  return (
    <button
      className="msg-menu-item"
      role="menuitem"
      style={{ alignItems: 'flex-start', opacity: isUnread ? 1 : 0.65 }}
      onClick={onOpen}
      title={href ? 'Open' : isUnread ? 'Mark as read' : undefined}
    >
      <span aria-hidden style={{ marginTop: 6, width: 7, height: 7, flex: '0 0 auto', borderRadius: 999, background: isUnread ? 'var(--accent)' : 'var(--line-strong)' }} />
      <span className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span style={{ fontWeight: isUnread ? 600 : 500 }}>{n.title}</span>
        {n.body && (
          <span className="muted" style={{ fontSize: 'var(--fs-xs)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {n.body}
          </span>
        )}
        <span className="muted t-xs">{formatDate(n.createdAt)}</span>
      </span>
    </button>
  );
}
